import {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { TabButton, type TabButtonState } from '../TabButton/TabButton';
import styles from './Tabs.module.css';

/**
 * Figma: `Tabs` (719:755) — the segmented bar: a `surface/background` track
 * holding `_Tab button base` segments 6px apart on 2px of padding.
 *
 * Three variant axes, of which two become props:
 *
 *   Full width  False | True  -> `fullWidth`
 *   Size        md | sm       -> `size`
 *   Type        Button border -> one option, so nothing to switch on
 *
 * `Type` is a single-option axis — the same shape `Breadcrumbs`' `State` axis
 * has — so it does not become a prop. Its name is also misleading: the bar has
 * no stroke. `strokes` is empty on all four variants, and the per-side stroke
 * weights bound to `border-width/1` are bound on a node with nothing to draw
 * them. Reported in plan.md §7.
 *
 *   md   28 tall (2 + 24 + 2), radius/md
 *   sm   20 tall (2 + 16 + 2), radius/sm
 *
 * Both hug vertically, so neither height is set here — they fall out of the
 * segment heights, which is what makes the 28 and the 20 correct by
 * construction rather than by assertion.
 *
 * `Full width=False` hugs its content and packs left (Figma `MIN`);
 * `Full width=True` is a fixed 1280 with every segment `FILL` and grow 1. 1280
 * is the canvas frame, not a property of the component, so it becomes `width:
 * 100%` — the bar fills whatever it is given.
 *
 * The tabs themselves are data, not structure. Figma hard-codes ten segments in
 * the hugging variants and five in the full-width ones, which an instance cannot
 * vary; `items` drives it here the way `Sidebar` takes `sections` and
 * `Breadcrumbs` takes `items`.
 *
 * ACCESSIBILITY — none of this is in Figma, which defines appearance only.
 *
 * The bar renders one of two things depending on what the items are, because
 * they are genuinely two different controls:
 *
 *   no `href`  a `tablist` of `tab`s that switch panels in place. Roving
 *              tabindex, arrow-key navigation, Home/End, `aria-selected`, and
 *              `aria-controls` when the caller supplies a panel id.
 *   any `href` a `<nav>` of links that change the page. The current one is
 *              marked `aria-current="page"`. No roving tabindex and no arrow
 *              keys — these are ordinary links and each should be its own tab
 *              stop.
 *
 * Figma cannot express the difference: it draws one bar. But the settings bar it
 * draws — "My details / Profile / Password / Team / Plan" — is exactly the case
 * that is sometimes routed and sometimes not, and announcing a set of links as
 * tab panels is wrong in a way a screen-reader user hits immediately.
 *
 * MOTION — not in Figma, which draws resting states. transitions.dev
 * `16-tabs-sliding`: one chip behind the bar slides and resizes to the selected
 * segment, instead of each segment painting its own and the selection blinking
 * from one to the next. See `placePill` below.
 */
export type TabsSize = 'md' | 'sm';

export type TabItem = {
  /** Stable identity, and the value reported by `onChange`. */
  value: string;
  label: string;
  /**
   * Figma's `Icon` boolean on the segment — a 12x12 glyph before the label.
   * Forwarded to `TabButton`, which documents why it is a slot and not an enum.
   */
  icon?: ReactNode;
  /**
   * Figma's `Badge` boolean. Drawn at `size="md"` only — the sm variants have no
   * counter layer. See the note in `TabButton`.
   */
  count?: number;
  /** Turns the bar into navigation — see the accessibility note above. */
  href?: string;
  /** `id` of the panel this tab controls, for `aria-controls`. */
  controls?: string;
};

export type TabsProps = {
  items: TabItem[];
  /** Controlled selection. */
  value?: string;
  /** Uncontrolled initial selection. Defaults to the first item. */
  defaultValue?: string;
  onChange?: (value: string) => void;
  size?: TabsSize;
  /** Figma `Full width=True` — segments share the bar's width equally. */
  fullWidth?: boolean;
  /**
   * Accessible name for the bar. Not in Figma — a page can hold more than one
   * tab bar, so this one has to say which it is.
   */
  label?: string;
  /** Force every unselected segment's state for documentation only. */
  state?: TabButtonState;
  className?: string;
};

export function Tabs({
  items,
  value,
  defaultValue,
  onChange,
  size = 'md',
  fullWidth = false,
  label = 'Tabs',
  state,
  className,
}: TabsProps) {
  const fallbackId = useId();
  const refs = useRef<(HTMLElement | null)[]>([]);
  const barRef = useRef<HTMLDivElement & HTMLElement>(null);
  const pillRef = useRef<HTMLSpanElement>(null);
  const [uncontrolled, setUncontrolled] = useState(
    () => defaultValue ?? items[0]?.value,
  );

  const selected = value ?? uncontrolled;
  /* Any href at all makes the whole bar navigation — a mixed bar is neither. */
  const isNav = items.some((item) => item.href !== undefined);

  const selectedIndex = items.findIndex((item) => item.value === selected);

  /*
   * The skill writes `offsetLeft` / `offsetWidth` onto the pill. Both round to
   * whole pixels, and a full-width segment is 250.4 wide, so the pill is placed
   * from `getBoundingClientRect` instead — but that one sees transforms, and a
   * segment is mid press-dip (`--press-scale`) at the moment it is clicked. The
   * dip scales about the centre, so the centre is exact either way; the size
   * comes from the computed width and height, which ignore transforms.
   *
   * An ANCESTOR's transform is the other trap: `Modal` opens from scale 0.96,
   * and a bar mounted inside it is first measured at that scale — which put
   * the pill 2.45px off in the full-width modal, permanently, since a transform
   * change fires no ResizeObserver. Dividing the rect offsets by the bar's
   * rendered-to-layout ratio takes every ancestor scale back out.
   *
   * `animate: false` is the skill's snap — first paint, resize — written with
   * the transition suspended and a reflow before it is restored. Resizes only
   * snap when the target actually moved, because a ResizeObserver reports once
   * on `observe()`, and snapping then would cut short the slide that the same
   * commit just started.
   */
  const lastPlacement = useRef<string | null>(null);
  const placePill = useCallback(
    (animate: boolean) => {
      const bar = barRef.current;
      const pill = pillRef.current;
      if (!bar || !pill) return;
      const tab = selectedIndex >= 0 ? refs.current[selectedIndex] : null;
      if (!tab) {
        pill.style.opacity = '0';
        lastPlacement.current = null;
        return;
      }

      const barBox = bar.getBoundingClientRect();
      const tabBox = tab.getBoundingClientRect();
      const barStyle = getComputedStyle(bar);
      const kx = barBox.width / parseFloat(barStyle.width) || 1;
      const ky = barBox.height / parseFloat(barStyle.height) || 1;
      const { width, height } = getComputedStyle(tab);
      const w = parseFloat(width);
      const h = parseFloat(height);
      const x = (tabBox.left + tabBox.width / 2 - barBox.left) / kx - w / 2 - bar.clientLeft;
      const y = (tabBox.top + tabBox.height / 2 - barBox.top) / ky - h / 2 - bar.clientTop;
      const placement = `${x} ${y} ${w} ${h}`;
      if (placement === lastPlacement.current) return;
      const first = lastPlacement.current === null;
      lastPlacement.current = placement;

      const snap = !animate || first;
      if (snap) pill.style.transition = 'none';
      pill.style.opacity = '';
      pill.style.translate = `${x}px ${y}px`;
      pill.style.width = `${w}px`;
      pill.style.height = `${h}px`;
      if (snap) {
        void pill.offsetWidth;
        pill.style.transition = '';
      }
    },
    [selectedIndex],
  );

  useLayoutEffect(() => {
    placePill(true);
  }, [placePill, items]);

  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const observer = new ResizeObserver(() => placePill(false));
    observer.observe(bar);
    refs.current.forEach((tab) => tab && observer.observe(tab));
    return () => observer.disconnect();
  }, [placePill, items]);

  const select = useCallback(
    (next: string) => {
      if (value === undefined) setUncontrolled(next);
      onChange?.(next);
    },
    [value, onChange],
  );

  /*
   * Arrow-key navigation, per the ARIA tabs pattern: Left/Right wrap around the
   * bar, Home/End jump to the ends, and selection follows focus. Not in Figma.
   * Only for the tablist form — links keep their native behaviour.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLElement>, index: number) => {
    const last = items.length - 1;
    let next: number | null = null;

    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;

    if (next === null) return;
    event.preventDefault();
    select(items[next].value);
    refs.current[next]?.focus();
  };

  const classes = [
    styles.bar,
    styles[size],
    fullWidth ? styles.fullWidth : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const segments = items.map((item, index) => {
    const current = item.value === selected;

    return (
      <TabButton
        key={item.value}
        ref={(node: HTMLElement | null) => {
          refs.current[index] = node;
        }}
        id={isNav ? undefined : `${fallbackId}-tab-${item.value}`}
        current={current}
        size={size}
        icon={item.icon}
        count={item.count}
        fullWidth={fullWidth}
        surface={false}
        href={item.href}
        state={current ? undefined : state}
        onClick={(event: MouseEvent<HTMLElement>) => {
          /*
           * A link navigates; letting it also flip local selection would show
           * the new tab as current before the route had actually changed.
           */
          if (isNav) return;
          event.preventDefault();
          select(item.value);
        }}
        onKeyDown={isNav ? undefined : (event) => onKeyDown(event, index)}
        role={isNav ? undefined : 'tab'}
        tabIndex={isNav ? undefined : current ? 0 : -1}
        aria-selected={isNav ? undefined : current}
        aria-controls={isNav ? undefined : item.controls}
        aria-current={isNav && current ? 'page' : undefined}
      >
        {item.label}
      </TabButton>
    );
  });

  /* Decorative — the selection is announced by `aria-selected` / `aria-current`. */
  const pill = <span ref={pillRef} className={styles.pill} aria-hidden="true" />;

  if (isNav) {
    return (
      <nav ref={barRef} className={classes} aria-label={label}>
        {pill}
        {segments}
      </nav>
    );
  }

  return (
    <div ref={barRef} className={classes} role="tablist" aria-label={label}>
      {pill}
      {segments}
    </div>
  );
}
